import * as THREE from 'three';
import { Sky } from 'three/examples/jsm/objects/Sky.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { Forest } from './Forest';
import { simClock } from './clock';
import { CARRIER_ORBIT_RADIUS } from './entities/SkyCarrier';
import firBoughUrl from './fir-bough.png';
import leafGreenUrl from './leaf-green.png';
import leafAutumnUrl from './leaf-autumn.png';
import mountainRockUrl from './mountain-rock.png';
import type { EntityCtx } from './Entity';

export interface MountainPeak {
  /** Horizontal centre (x,z) + the 70%-height "peak" point used for placing things on the rock. */
  pos: THREE.Vector3;
  /** Base radius of the collision cone (widest, at `baseY`), tapering to 0 at `summitY`. */
  radius: number;
  /** True summit y (top of the cone), used by anything that wants to sit on the peak. */
  summitY: number;
  /** Base y (foot of the cone, on the terrain). Collision spans baseY→summitY so you can't fly
   *  UNDER the rock near the deck — the old single-sphere hitbox left the base uncovered. */
  baseY: number;
}

/** A single hittable building. Tracked individually so strafing fire can take one out
 *  without flattening the entire village. */
export class Building {
  destroyed = false;
  constructor(
    public position: THREE.Vector3,
    public radius: number,
    public mesh: THREE.Object3D,
    public village: Village,
  ) {}

  destroy(parent: THREE.Object3D): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.mesh.visible = false;
    // Small per-building rubble pile in place of the building.
    const pile = makeBuildingRubble(this.position, this.radius);
    parent.add(pile);
    this.village.onBuildingDestroyed();
  }
}

/** A bombable village — holds its world center, the intact-buildings group (for hiding
 *  on destruction), per-building references, and a flag the bomb code reads. */
export class Village {
  destroyed = false;
  rubbleGroup: THREE.Group | null = null;
  buildings: Building[] = [];
  /** Game seconds when this village last lost a building — drives "UNDER ATTACK" alerts. */
  lastDamageAt = -999;
  constructor(
    public position: THREE.Vector3,
    public radius: number,
    public intactGroup: THREE.Group,
  ) {}

  onBuildingDestroyed() {
    // Stamp the moment (SIM clock) so the threat system can flag this village as under attack —
    // read back against the same sim clock in Game's threat/map/radar code.
    this.lastDamageAt = simClock.now;
    // If every building is gone, the village counts as destroyed too.
    if (this.destroyed) return;
    if (this.buildings.every((b) => b.destroyed)) {
      this.destroyed = true;
    }
  }

  /** Bomb wipes the whole village: hide everything and drop a wide rubble field. */
  destroy(parent: THREE.Object3D): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.intactGroup.visible = false;
    for (const b of this.buildings) b.destroyed = true;
    const rubble = makeRubble(this.position, this.radius);
    parent.add(rubble);
    this.rubbleGroup = rubble;
  }
}

export class Scene {
  readonly sky: Sky;
  readonly sun = new THREE.Vector3();
  readonly dirLight: THREE.DirectionalLight;
  readonly terrain: THREE.Mesh;
  readonly water: THREE.Mesh;
  readonly clouds: THREE.Group;
  /** Shared cloud sprite materials, counter-rolled each frame to keep the puffs level when banking. */
  private cloudMats: THREE.SpriteMaterial[] = [];
  readonly mountains: THREE.Group;
  readonly peaks: MountainPeak[] = [];
  readonly villages: Village[] = [];
  /** Trench centreline (world XZ) — the front line that impedes tanks and is garrisoned. */
  readonly trenchPath: THREE.Vector2[] = [];
  /** The scene root — used by bombs to attach rubble groups. */
  readonly root: THREE.Scene;
  private riverMat?: THREE.ShaderMaterial;
  /** Packed, individually-fellable forest (see Forest.ts). */
  private forest = new Forest();

  /** World ground height at (x, z) — for placing/keeping ground units on the terrain. */
  heightAt(x: number, z: number): number { return terrainHeight(x, z); }

  /** Fell every tree within `radius` of (x, z) — used by crash impacts. Returns felled positions. */
  fellTrees(x: number, z: number, radius: number): THREE.Vector3[] {
    return this.forest.fell(x, z, radius);
  }

  /** Shortest distance from (x, z) to the trench centreline. */
  private distToTrench(x: number, z: number): number {
    return distToPath(x, z, this.trenchPath);
  }

  /** Movement multiplier for a ground vehicle at (x, z): 1 on open ground, dropping toward
   *  TRENCH_MIN at the trench centreline so tanks crawl across the parapets and are exposed. */
  trenchSlowFactor(x: number, z: number): number {
    if (this.trenchPath.length < 2) return 1;
    const d = this.distToTrench(x, z);
    if (d >= TRENCH_BAND) return 1;
    const t = d / TRENCH_BAND;
    return TRENCH_MIN + (1 - TRENCH_MIN) * t * t;
  }

  /** Evenly-spaced points along the trench, seated on the terrain, for placing its garrison. */
  trenchGarrisonPoints(n: number): THREE.Vector3[] {
    const path = this.trenchPath;
    const out: THREE.Vector3[] = [];
    if (path.length < 2) return out;
    for (let k = 0; k < n; k++) {
      const p = path[Math.floor(((k + 0.5) / n) * (path.length - 1))];
      out.push(new THREE.Vector3(p.x, terrainHeight(p.x, p.y), p.y));
    }
    return out;
  }

  constructor(root: THREE.Scene) {
    this.root = root;
    // Background default so even before Sky renders we see "sky-ish"
    root.background = new THREE.Color(0x88b7e0);
    root.fog = new THREE.Fog(0xbcd7ee, 2400, 10000);

    // Sun dir
    const phi = THREE.MathUtils.degToRad(90 - 18);
    const theta = THREE.MathUtils.degToRad(-40);
    this.sun.setFromSphericalCoords(1, phi, theta);

    // Sky
    this.sky = new Sky();
    this.sky.scale.setScalar(40000);
    const s = this.sky.material.uniforms;
    s.turbidity.value = 6;
    s.rayleigh.value = 2;
    s.mieCoefficient.value = 0.005;
    s.mieDirectionalG.value = 0.8;
    s.sunPosition.value.copy(this.sun);
    root.add(this.sky);

    // Lights — sun-ish directional + hemi. Shadows disabled because the player roams
    // thousands of units from origin; a fixed shadow frustum can't follow them cheaply.
    this.dirLight = new THREE.DirectionalLight(0xffe4b5, 2.8);
    this.dirLight.position.copy(this.sun).multiplyScalar(2000);
    this.dirLight.castShadow = false;
    root.add(this.dirLight);
    root.add(new THREE.HemisphereLight(0xaed7ff, 0x5b8a50, 0.55));

    // Plan village sites FIRST — the terrain levels its ground beneath each settlement, and
    // the building placement reads the same pads, so structures sit flush on the leveled land.
    this.planVillages();

    // Terrain
    this.terrain = this.makeTerrain();
    root.add(this.terrain);

    // River water surface (animated) — fills the carved channel.
    this.water = this.makeRiver();
    root.add(this.water);

    // Clouds
    this.clouds = this.makeClouds();
    root.add(this.clouds);

    // Mountains
    this.mountains = this.makeMountains();
    root.add(this.mountains);

    // Landmarks
    root.add(this.makeLandmarks());
  }

  /** Choose village sites, spaced apart and clear of the river, and record a leveled pad for
   *  each so the terrain flattens beneath it. Fills the module-level VILLAGE_PADS. */
  private planVillages(): void {
    VILLAGE_PADS.length = 0;
    const tline = trenchCenterline();
    for (let v = 0; v < 20; v++) {
      const flatR = 190 + Math.random() * 40;
      const r = flatR + 150;
      let cx = 0, cz = 0, ok = false;
      for (let attempt = 0; attempt < 40; attempt++) {
        cx = (Math.random() - 0.5) * 7200;
        cz = (Math.random() - 0.5) * 7200;
        if (riverDist(cx, cz) <= 260 || Math.hypot(cx, cz) >= 5200) continue;
        // Keep the WHOLE pad clear of the trench so buildings never overlap the earthwork.
        if (distToPath(cx, cz, tline) < r + 60) continue;
        // Keep villages spread out so their leveled pads don't terrace into each other.
        if (VILLAGE_PADS.some((p) => Math.hypot(p.x - cx, p.z - cz) < 760)) continue;
        ok = true; break;
      }
      if (!ok) continue;
      VILLAGE_PADS.push({ x: cx, z: cz, flatR, r, level: terrainBaseDisplacement(cx, cz) });
    }
  }

  /** Animated river surface: a ribbon following the river centerline, sitting in the carved
   *  channel just below the bank, with gerstner ripples, fresnel sky reflection and a sun glint. */
  private makeRiver(): THREE.Mesh {
    const LEN = 16000;
    const WID = RIVER.half * 1.7; // stays inside the carved banks
    const geom = new THREE.PlaneGeometry(LEN, WID, 320, 6); // XY plane, +Z normal
    const dirx = Math.cos(RIVER.ang), dirz = Math.sin(RIVER.ang); // along the river line
    const nx = -Math.sin(RIVER.ang), nz = Math.cos(RIVER.ang);    // across it
    const pos = geom.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      const u = pos.getX(i); // along length
      const w = pos.getY(i); // across width
      const wx = RIVER.x0 + u * dirx + w * nx;
      const wz = RIVER.z0 + u * dirz + w * nz;
      const wy = TERRAIN_BASE_Y + terrainBaseDisplacement(wx, wz) - 3.2; // fill to just below the bank
      pos.setXYZ(i, wx, wy, wz);
    }
    geom.computeVertexNormals();
    const mat = makeWaterMaterial(this.sun);
    this.riverMat = mat;
    const mesh = new THREE.Mesh(geom, mat);
    mesh.renderOrder = 1;
    mesh.frustumCulled = false;
    return mesh;
  }

  private makeTerrain(): THREE.Mesh {
    const size = 16000;
    const seg = 384;
    const geom = new THREE.PlaneGeometry(size, size, seg, seg);
    geom.rotateX(-Math.PI / 2);
    const pos = geom.attributes.position as THREE.BufferAttribute;
    // Per-vertex colour: meadow/field/dry-earth patchwork from low-freq noise, slope-darkened,
    // with sandy banks and blue water blended along the carved river.
    const colors = new Float32Array(pos.count * 3);
    const meadow = new THREE.Color(0x647f44);
    const field = new THREE.Color(0x83995a);
    const dry = new THREE.Color(0x9c8f5c);
    const rock = new THREE.Color(0x6b6450);
    const sand = new THREE.Color(0xb7a878);
    const water = new THREE.Color(0x2e5d7e);
    const tmp = new THREE.Color();
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i), z = pos.getZ(i);
      const h = terrainDisplacement(x, z);
      pos.setY(i, h);
      // Patchwork of fields.
      const n = 0.5 + 0.5 * Math.sin(x * 0.0017 + z * 0.0011) * Math.cos(z * 0.0023 - x * 0.0007);
      const dryMix = THREE.MathUtils.clamp(0.5 + 0.5 * Math.sin(x * 0.0009 - z * 0.0013), 0, 1);
      tmp.copy(meadow).lerp(field, n);
      tmp.lerp(dry, dryMix * 0.32);
      // Slope (finite difference) → darken/rock the steeper folds for relief.
      const slope = Math.min(1, Math.hypot(
        terrainDisplacement(x + 12, z) - terrainDisplacement(x - 12, z),
        terrainDisplacement(x, z + 12) - terrainDisplacement(x, z - 12)) / 6);
      tmp.lerp(rock, slope * 0.55);
      tmp.multiplyScalar(0.9 + (h / 20) * 0.1); // hollows darker, rises lighter
      // River banks + water.
      const d = riverDist(x, z);
      if (d < RIVER.half + 30) {
        const bank = THREE.MathUtils.clamp((RIVER.half + 30 - d) / 30, 0, 1);
        tmp.lerp(sand, bank * 0.5);
      }
      if (d < RIVER.half * 0.72) {
        const wt = THREE.MathUtils.clamp((RIVER.half * 0.72 - d) / (RIVER.half * 0.72), 0, 1);
        tmp.lerp(water, wt);
      }
      colors[i * 3] = tmp.r; colors[i * 3 + 1] = tmp.g; colors[i * 3 + 2] = tmp.b;
    }
    geom.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geom.computeVertexNormals();
    const mat = new THREE.MeshStandardMaterial({
      roughness: 1,
      metalness: 0,
      vertexColors: true,
      // Push terrain fragments slightly AWAY from the camera in the depth buffer so
      // flat decals sitting on top (trenches, river, roads) render without z-fighting.
      polygonOffset: true,
      polygonOffsetFactor: 1,
      polygonOffsetUnits: 1,
    });
    const mesh = new THREE.Mesh(geom, mat);
    mesh.position.y = -500;
    mesh.receiveShadow = true;
    return mesh;
  }

  private makeClouds(): THREE.Group {
    const group = new THREE.Group();
    // Three distinct puff textures across three brightness tiers → varied, fuller cumulus.
    const texes = [makeCloudTexture(), makeCloudTexture(), makeCloudTexture()];
    const lowMats = [0.95, 0.82, 0.66].map((o, i) =>
      new THREE.SpriteMaterial({ map: texes[i], transparent: true, opacity: o, depthWrite: false }));
    for (let i = 0; i < 160; i++) {
      const s = new THREE.Sprite(lowMats[i % 3]);
      let r = 700 + Math.random() * 4900;
      // Keep the Sky Carrier's orbit ring clear of cumulus — the wave-6 boss flies at the cloud
      // band's altitude and was reduced to fog ghosts. Nudge lane-crossing puffs to either side.
      if (Math.abs(r - CARRIER_ORBIT_RADIUS) < 320) {
        r = r < CARRIER_ORBIT_RADIUS ? CARRIER_ORBIT_RADIUS - 320 - Math.random() * 200
                                     : CARRIER_ORBIT_RADIUS + 320 + Math.random() * 200;
      }
      const a = Math.random() * Math.PI * 2;
      s.position.set(Math.cos(a) * r, 10 + Math.random() * 210, Math.sin(a) * r);
      const scale = 220 + Math.random() * 460;
      s.scale.set(scale, scale * (0.46 + Math.random() * 0.2), 1);
      group.add(s);
    }
    // High, thin haze band for atmospheric depth above the action.
    const hazeMat = new THREE.SpriteMaterial({ map: texes[0], transparent: true, opacity: 0.22, depthWrite: false, color: 0xdde8f4 });
    for (let i = 0; i < 44; i++) {
      const s = new THREE.Sprite(hazeMat);
      const r = 1400 + Math.random() * 5200;
      const a = Math.random() * Math.PI * 2;
      s.position.set(Math.cos(a) * r, 330 + Math.random() * 220, Math.sin(a) * r);
      const scale = 620 + Math.random() * 760;
      s.scale.set(scale, scale * 0.38, 1);
      group.add(s);
    }
    // Shared materials we counter-roll each frame (see update) so the billboards stay world-upright.
    this.cloudMats = [...lowMats, hazeMat];
    return group;
  }

  private makeMountains(): THREE.Group {
    const group = new THREE.Group();
    const ringRadius = 6000;
    const count = 80;
    // Single shared material is flatShaded + uses vertex colors so each mountain can
    // carry its own per-vertex gradient (rocky base → gray mid → white snow cap). A triplanar
    // rock texture is injected below to grain the facets so they don't read as flat solid colours
    // ("less simulated"). Triplanar (world-space projection) avoids the stretched cone UVs.
    const mat = new THREE.MeshStandardMaterial({
      roughness: 1, metalness: 0, flatShading: true, vertexColors: true,
    });
    mat.onBeforeCompile = (shader) => {
      shader.uniforms.rockMap = { value: ROCK_TEX };
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vRockWPos;\nvarying vec3 vRockWNrm;')
        .replace('#include <begin_vertex>',
          '#include <begin_vertex>\n  vRockWPos = (modelMatrix * vec4(transformed, 1.0)).xyz;\n  vRockWNrm = mat3(modelMatrix) * normal;');
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', '#include <common>\nuniform sampler2D rockMap;\nvarying vec3 vRockWPos;\nvarying vec3 vRockWNrm;')
        .replace('#include <color_fragment>', `#include <color_fragment>
        {
          // Blend the three planar projections by the world normal (sharpened) — no UV seams.
          vec3 bw = pow(abs(normalize(vRockWNrm)), vec3(4.0));
          bw /= (bw.x + bw.y + bw.z);
          float sc = 0.01; // fine grain — mostly for the (rare) close-up view
          vec3 rk = texture2D(rockMap, vRockWPos.yz * sc).rgb * bw.x
                  + texture2D(rockMap, vRockWPos.xz * sc).rgb * bw.y
                  + texture2D(rockMap, vRockWPos.xy * sc).rgb * bw.z;
          // MACRO octave — the tonal zones large enough (~2-3 per peak) to still read at the
          // 6000u backdrop distance through fog. This is the part you actually see in play, so it
          // dominates the mix.
          float mc = sc * 0.32;
          vec3 rkM = texture2D(rockMap, vRockWPos.yz * mc).rgb * bw.x
                   + texture2D(rockMap, vRockWPos.xz * mc).rgb * bw.y
                   + texture2D(rockMap, vRockWPos.xy * mc).rgb * bw.z;
          float grain = dot(rk, vec3(0.3333)) * 0.3 + dot(rkM, vec3(0.3333)) * 0.7;
          // Fade the rock texture out on the bright snow caps so they stay clean.
          float lum = dot(diffuseColor.rgb, vec3(0.299, 0.587, 0.114));
          float rockMask = 1.0 - smoothstep(0.5, 0.72, lum);
          diffuseColor.rgb *= mix(1.0, 0.32 + grain * 1.35, rockMask);
        }`);
    };
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2 + (Math.random() - 0.5) * 0.04;
      const jitter = (Math.random() - 0.5) * 500;
      const r = ringRadius + jitter;
      // Mixed heights: tall scenic peaks you crash into + lower saddles you can climb OVER to get
      // past the ring (the intended way out). Base at y=-500, so summit = -500 + height. Halfway
      // between the original towering range (900-1600) and the lowered one (380-1200).
      const height = 640 + Math.random() * 760; // 640–1400 → summit 140..900
      const base = 560 + Math.random() * 360;
      const seed = Math.random() * 1000;
      const geom = buildFractalMountain(base, height, seed);
      const m = new THREE.Mesh(geom, mat);
      m.position.set(Math.cos(a) * r, -500 + height / 2, Math.sin(a) * r);
      m.rotation.y = Math.random() * Math.PI;
      m.castShadow = false;
      m.receiveShadow = false;
      group.add(m);
      const peakPos = m.position.clone();
      peakPos.y += height * 0.2;
      const summitY = m.position.y + height / 2; // top of the cone in world units
      const baseY = m.position.y - height / 2;   // foot of the cone, on the terrain (≈ -500)
      this.peaks.push({ pos: peakPos, radius: Math.max(base * 0.85, 360), summitY, baseY });
    }
    return group;
  }

  private makeLandmarks(): THREE.Group {
    const g = new THREE.Group();
    // Lossless draw-call batching is on by default; ?nomerge=1 disables it for an A/B pixel check.
    const mergeBuildings = !(typeof location !== 'undefined' && new URLSearchParams(location.search).get('nomerge') === '1');
    // (The river is now carved + tinted into the terrain itself — no separate plane.)

    // Trench — a CONTINUOUS fire trench: a dark recessed channel flanked by raised earth
    // parapets (the dug spoil), crenellated into fire-bays so it reads as an earthwork from
    // the air rather than a row of loose boxes. Each segment follows the terrain under it.
    const trenchEarth = new THREE.MeshStandardMaterial({ color: 0x6b5333, roughness: 1, flatShading: true });
    const trenchEarthDk = new THREE.MeshStandardMaterial({ color: 0x55421f, roughness: 1, flatShading: true });
    const trenchFloor = new THREE.MeshStandardMaterial({ color: 0x221a10, roughness: 1, flatShading: true });
    const sandbagMat = new THREE.MeshStandardMaterial({ color: 0x837049, roughness: 1, flatShading: true });
    const trench = new THREE.Group();
    const tPts = trenchCenterline();
    this.trenchPath.push(...tPts); // expose the centreline for tank-blocking + garrison placement
    for (let i = 0; i < tPts.length - 1; i++) {
      const a = tPts[i], b = tPts[i + 1];
      const dx = b.x - a.x, dz = b.y - a.y;
      const len = Math.hypot(dx, dz);
      const mx = (a.x + b.x) / 2, mz = (a.y + b.y) / 2;
      const seg = new THREE.Group();
      seg.position.set(mx, terrainHeight(mx, mz), mz);
      seg.rotation.y = Math.atan2(-dz, dx); // align local +X with the segment
      // Dark recessed channel / duckboard floor.
      const floor = new THREE.Mesh(new THREE.BoxGeometry(len + 8, 3, 16), trenchFloor);
      floor.position.y = 0.4;
      seg.add(floor);
      // Raised earth parapets flanking the channel.
      for (const sgn of [-1, 1]) {
        const berm = new THREE.Mesh(new THREE.BoxGeometry(len + 8, 6.5 + (i % 3), 11, 1, 1, 1), i % 2 ? trenchEarth : trenchEarthDk);
        berm.position.set(0, 2.6, sgn * 13);
        seg.add(berm);
      }
      // Sandbag courses along the front (-Z) parapet on alternating bays.
      if (i % 2 === 0) {
        for (let s = -1; s <= 1; s++) {
          const bag = new THREE.Mesh(new THREE.BoxGeometry(len * 0.27, 2.2, 4.5, 1, 1, 1), sandbagMat);
          bag.position.set(s * len * 0.3, 6.2, -13);
          seg.add(bag);
        }
      }
      trench.add(seg);
    }
    g.add(trench);

    // --- Village sites: planned in planVillages() so the terrain is already leveled for them. ---
    const villageCenters = VILLAGE_PADS.map((p) => ({ cx: p.x, cz: p.z }));
    const nearVillage = (x: number, z: number, m: number) =>
      VILLAGE_PADS.some((p) => Math.hypot(p.x - x, p.z - z) < p.r + m);

    // --- Forest: trees clustered into GROVES (not uniform scatter), keeping clear of the
    //     river, the villages, the mountain ring AND each other (no interpenetrating canopies). ---
    const barkMat = new THREE.MeshStandardMaterial({ color: 0x3d2a1a, roughness: 0.95, flatShading: true });
    const placedTrees: { x: number; z: number }[] = [];
    const TREE_GAP = 30; // minimum spacing between trunks
    const treeClear = (x: number, z: number) =>
      riverDist(x, z) >= 130 && !nearVillage(x, z, 50) && Math.hypot(x, z) <= 5400 &&
      this.distToTrench(x, z) >= 24 &&
      !placedTrees.some((p) => Math.hypot(p.x - x, p.z - z) < TREE_GAP);
    // Trees are collected into the packed Forest (Forest.ts): every tree of a type shares a vertex
    // count, so they pack into fixed-size buffer slots that render in ~2 draw calls per type AND let
    // an individual tree be felled by a crash (swap-remove a slot). Same pixels as the old global
    // merge, but destructible.
    for (let gi = 0; gi < 18; gi++) {
      let gx = 0, gz = 0;
      for (let attempt = 0; attempt < 20; attempt++) {
        const a = Math.random() * Math.PI * 2;
        const rr = 1300 + Math.random() * 3500;
        gx = Math.cos(a) * rr; gz = Math.sin(a) * rr;
        if (riverDist(gx, gz) > 220 && !nearVillage(gx, gz, 120)) break;
      }
      const groveR = 240 + Math.random() * 430;
      const treeCount = 10 + Math.floor(Math.random() * 20);
      for (let t = 0; t < treeCount; t++) {
        // A few tries to find an un-crowded spot; skip this tree if the grove is already full.
        let x = 0, z = 0, ok = false;
        for (let tryN = 0; tryN < 6 && !ok; tryN++) {
          const rr = groveR * Math.sqrt(Math.random()); // denser toward the grove centre
          const a = Math.random() * Math.PI * 2;
          x = gx + Math.cos(a) * rr; z = gz + Math.sin(a) * rr;
          ok = treeClear(x, z);
        }
        if (!ok) continue;
        placedTrees.push({ x, z });
        const gy = terrainHeight(x, z);
        const kind = Math.random();
        if (kind < 0.62) this.forest.add('conifer', makeConifer(x, gy, z), x, z, gy);
        else if (kind < 0.9) { const autumn = Math.random() < 0.18; this.forest.add(autumn ? 'autumn' : 'broadleaf', makeBroadleaf(x, gy, z, autumn), x, z, gy); }
        else this.forest.add('dead', makeDeadTree(x, gy, z, barkMat), x, z, gy);
      }
    }
    // Sparse lone trees between the groves so the open country isn't bare.
    for (let i = 0; i < 80; i++) {
      const a = Math.random() * Math.PI * 2, rr = 1200 + Math.random() * 3900;
      const x = Math.cos(a) * rr, z = Math.sin(a) * rr;
      if (!treeClear(x, z)) continue;
      placedTrees.push({ x, z });
      const gy = terrainHeight(x, z);
      if (Math.random() < 0.6) this.forest.add('conifer', makeConifer(x, gy, z), x, z, gy);
      else { const autumn = Math.random() < 0.18; this.forest.add(autumn ? 'autumn' : 'broadleaf', makeBroadleaf(x, gy, z, autumn), x, z, gy); }
    }

    // Villages — each building is individually shaped/colored/oriented.
    const wallPalette = [0xb08a5a, 0xc7a579, 0x9a7044, 0xb59269, 0xa07a4a, 0xcdb693, 0x8a6a3e];
    const roofPalette = [0x8a3b2f, 0x6f3224, 0x4d2a1c, 0x7a4a32, 0x8d5436, 0x5d3424];
    const stoneMat = new THREE.MeshStandardMaterial({ color: 0x9a9690, roughness: 1 });
    const slateMat = new THREE.MeshStandardMaterial({ color: 0x3a3d42, roughness: 1 });

    for (const { cx, cz } of villageCenters) {
      const wallMatBase = wallPalette[Math.floor(Math.random() * wallPalette.length)];

      const villageGroup = new THREE.Group();
      const village = new Village(new THREE.Vector3(cx, terrainHeight(cx, cz), cz), 220, villageGroup);

      const addBuilding = (mesh: THREE.Object3D, ox: number, oz: number, radius: number) => {
        // Merge happens as a POST-PROCESS after all generation (see below), NOT here — doing it
        // inline would create merged geometries mid-generation, and THREE.generateUUID() burns
        // Math.random per geometry, which (under a seeded RNG) would shift later placement.
        villageGroup.add(mesh);
        village.buildings.push(new Building(
          new THREE.Vector3(ox, terrainHeight(ox, oz) + 8, oz), radius, mesh, village,
        ));
      };

      // Pick a primary street angle and a perpendicular cross street.
      const mainAngle = Math.random() * Math.PI;
      const mainCos = Math.cos(mainAngle), mainSin = Math.sin(mainAngle);
      const crossAngle = mainAngle + Math.PI / 2;
      const crossCos = Math.cos(crossAngle), crossSin = Math.sin(crossAngle);

      // Visible street strips. Add the cross street first so the main street's
      // polygonOffset wins at the intersection.
      villageGroup.add(makeStreet(cx, cz, crossAngle, 240, 10));
      villageGroup.add(makeStreet(cx, cz, mainAngle, 320, 12));

      // Lay out buildings along each street with a fixed spacing so they don't overlap.
      // `setback` is the perpendicular distance from the street centerline to the
      // building footprint center; `spacing` is the gap between consecutive lots.
      const SETBACK = 28;
      const SPACING = 56;

      // Track placed footprints so we don't put a cross-street building on top of a
      // main-street one near the intersection.
      const placed: { x: number; z: number; r: number }[] = [];
      const tooClose = (x: number, z: number, r: number) => {
        for (const p of placed) {
          if (Math.hypot(p.x - x, p.z - z) < (p.r + r) * 0.95) return true;
        }
        return false;
      };

      // Church on its own plaza off the main street, halfway down one side.
      const churchT = (Math.random() < 0.5 ? -1 : 1) * 70;
      const churchSide = Math.random() < 0.5 ? -1 : 1;
      const churchSetback = SETBACK + 18;
      const churchX = cx + mainCos * churchT + (-mainSin) * churchSetback * churchSide;
      const churchZ = cz + mainSin * churchT + ( mainCos) * churchSetback * churchSide;
      // Front of church faces the street (toward the street point at churchT).
      const churchFaceYaw = Math.atan2(mainCos * churchT - mainCos * churchT, 0); // unused; compute via dx/dz below.
      const churchDx = churchX - (cx + mainCos * churchT);
      const churchDz = churchZ - (cz + mainSin * churchT);
      const churchYaw = Math.atan2(churchDx, churchDz);
      void churchFaceYaw;
      addBuilding(makeChurch(churchX, churchZ, stoneMat, slateMat, churchYaw), churchX, churchZ, 36);
      placed.push({ x: churchX, z: churchZ, r: 36 });

      const placeAlong = (
        angle: number,
        halfLength: number,
        side: -1 | 1,
        sCos: number,
        sSin: number,
      ) => {
        // -sin/cos perpendicular for left side (+1)
        const perpX = -sSin * side;
        const perpZ =  sCos * side;
        // Offset along street from -halfLength..+halfLength stepping by SPACING.
        // Skip a small gap centered on the intersection so the streets stay clear.
        for (let t = -halfLength; t <= halfLength; t += SPACING) {
          if (Math.abs(t) < 24) continue; // intersection clear
          // Tiny per-lot jitter so the row isn't perfectly mechanical.
          const tj = t + (Math.random() - 0.5) * 4;
          const jitter = (Math.random() - 0.5) * 4;
          const setbackJ = SETBACK + jitter;
          const bx = cx + sCos * tj + perpX * setbackJ;
          const bz = cz + sSin * tj + perpZ * setbackJ;
          const radius = 24;
          if (tooClose(bx, bz, radius)) continue;
          // Building's local -Z (front) points back toward the street centerline at this t.
          const sx = cx + sCos * tj;
          const sz = cz + sSin * tj;
          const yaw = Math.atan2(bx - sx, bz - sz);
          // Pick a building type
          const rnd = Math.random();
          let mesh: THREE.Object3D;
          let r: number;
          if (rnd < 0.18) {
            mesh = makeBarn(bx, bz, wallPalette[Math.floor(Math.random() * wallPalette.length)], roofPalette[Math.floor(Math.random() * roofPalette.length)], yaw);
            r = 32;
          } else if (rnd < 0.40) {
            mesh = makeTownhouse(bx, bz, wallMatBase + Math.floor(Math.random() * 0x050505) - 0x020202, roofPalette[Math.floor(Math.random() * roofPalette.length)], yaw);
            r = 22;
          } else if (rnd < 0.62) {
            mesh = makeGabledCottage(bx, bz, wallPalette[Math.floor(Math.random() * wallPalette.length)], roofPalette[Math.floor(Math.random() * roofPalette.length)], yaw);
            r = 26;
          } else if (rnd < 0.80) {
            mesh = makeLHouse(bx, bz, wallPalette[Math.floor(Math.random() * wallPalette.length)], roofPalette[Math.floor(Math.random() * roofPalette.length)], yaw);
            r = 28;
          } else {
            mesh = makeCottage(bx, bz, wallPalette[Math.floor(Math.random() * wallPalette.length)], roofPalette[Math.floor(Math.random() * roofPalette.length)], yaw);
            r = 24;
          }
          addBuilding(mesh, bx, bz, r);
          placed.push({ x: bx, z: bz, r });
        }
        void angle; // angle is implicit in (sCos, sSin)
      };

      // Both sides of the main street, then both sides of the cross street.
      placeAlong(mainAngle, 145, +1, mainCos, mainSin);
      placeAlong(mainAngle, 145, -1, mainCos, mainSin);
      placeAlong(crossAngle, 100, +1, crossCos, crossSin);
      placeAlong(crossAngle, 100, -1, crossCos, crossSin);

      g.add(villageGroup);
      this.villages.push(village);
    }

    // --- POST-PROCESS DRAW-CALL MERGE (lossless) ---------------------------------------------
    // All RNG-driven generation is complete above, so collapsing geometry here cannot change the
    // layout (THREE.generateUUID() consumes Math.random per created geometry, so doing this inline
    // would have shifted later placement). Merge by material: same vertices, same materials, far
    // fewer draw calls. ?nomerge=1 skips it for an A/B check.
    // ?notrees=1 omits the forest entirely — used to benchmark the forest's true frame-time cost
    // (after merging) so we know whether tree LOD is even worth the visual risk.
    const noTrees = typeof location !== 'undefined' && new URLSearchParams(location.search).get('notrees') === '1';
    if (mergeBuildings) {
      // Trees → packed, destructible slot buffers (~2 draw calls/type, individually fellable).
      if (!noTrees) g.add(this.forest.build());
      // Each building → its own merged Group, swapped in place so Building.destroy() still hides it.
      for (const village of this.villages) {
        for (const b of village.buildings) {
          const merged = mergeStaticByMaterial(b.mesh as THREE.Group);
          const parent = b.mesh.parent;
          if (parent) { parent.remove(b.mesh); parent.add(merged); }
          b.mesh = merged;
        }
      }
    } else {
      // ?nomerge debug A/B: raw tree objects, no packing, no felling.
      if (!noTrees) for (const obj of this.forest.rawObjects()) g.add(obj);
    }
    return g;
  }

  update(_ctx: EntityCtx) {
    // Drift clouds slowly
    const t = _ctx.now * 0.02;
    this.clouds.rotation.y = t * 0.05;
    // Keep the billboard clouds level with the horizon when you bank. A THREE.Sprite locks its "up"
    // to the camera's up vector, so it rolls with the camera; counter-rotate the shared cloud
    // materials so the sprite up instead tracks WORLD up. From the sprite vertex shader the texture
    // up maps to (-sin φ, cos φ) in view space, so aligning it with world-up (view-space (right.y,
    // up.y)) needs φ = atan2(-right.y, up.y) = atan2(-m[1], m[5]).
    const cam = _ctx.game.camera;
    if (cam) {
      cam.updateWorldMatrix(true, false);
      const m = cam.matrixWorld.elements;
      const rot = Math.atan2(-m[1], m[5]);
      for (const mat of this.cloudMats) mat.rotation = rot;
    }
    // Animate the river surface.
    if (this.riverMat) this.riverMat.uniforms.uTime.value = _ctx.now;
  }
}

/** Base Y of the terrain mesh (its `position.y`). The per-vertex displacement is added on top. */
const TERRAIN_BASE_Y = -500;

/** River centreline: a line through R0 along direction `ang`. The terrain is carved down
 *  and tinted along it, so the river is part of the ground (no separate plane that z-fights). */
const RIVER = { x0: -900, z0: 500, ang: 0.32, half: 110, depth: 9 };

/** The trench impedes ground vehicles: a tank within TRENCH_BAND of the line crawls (down to
 *  TRENCH_MIN of its speed) while it climbs the parapets — pinning it in the open as a target. */
const TRENCH_BAND = 28;
const TRENCH_MIN = 0.16;
export function riverDist(x: number, z: number): number {
  // perpendicular distance from (x,z) to the river line
  const nx = -Math.sin(RIVER.ang), nz = Math.cos(RIVER.ang);
  return Math.abs((x - RIVER.x0) * nx + (z - RIVER.z0) * nz);
}

/** Deterministic trench centreline (world XZ): a crenellated, gently meandering fire-trench.
 *  The single source of truth shared by the trench builder, the tank-impedance query, and
 *  village/tree placement (so they give the earthwork a wide berth and never overlap it). */
function trenchCenterline(): THREE.Vector2[] {
  const pts: THREE.Vector2[] = [];
  const startX = -2900, baseZ = -1150, step = 72, bays = 44;
  for (let i = 0; i <= bays; i++) {
    const x = startX + i * step;
    const cren = (i % 2) * 36; // fire-bay crenellation
    const meander = Math.sin(x * 0.0006) * 170 + Math.sin(x * 0.0017 + 1) * 45;
    pts.push(new THREE.Vector2(x, baseZ + cren + meander));
  }
  return pts;
}

/** Shortest distance from (x, z) to a polyline given as XZ points. */
function distToPath(x: number, z: number, path: THREE.Vector2[]): number {
  let best = Infinity;
  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i], b = path[i + 1];
    const abx = b.x - a.x, abz = b.y - a.y;
    const len2 = abx * abx + abz * abz;
    let t = len2 > 0 ? ((x - a.x) * abx + (z - a.y) * abz) / len2 : 0;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
    const d = Math.hypot(x - (a.x + abx * t), z - (a.y + abz * t));
    if (d < best) best = d;
  }
  return best;
}

/** Vertical displacement of the terrain at (x, z): layered rolling hills minus a carved
 *  river channel. The single source of truth shared by the terrain mesh AND everything that
 *  sits on the ground (units, buildings, decals) so nothing floats or z-fights. */
/** A village levels the ground it was built on: a flat core out to `flatR`, ramping back to
 *  natural relief by `r`. Planned once (deterministically) so the terrain mesh and the
 *  building placement agree. Populated by Scene.planVillages() before the terrain is built. */
export type VillagePad = { x: number; z: number; flatR: number; r: number; level: number };
export const VILLAGE_PADS: VillagePad[] = [];

/** Rolling-hill displacement WITHOUT the river carve or village leveling — the natural land
 *  surface. Used by the water plane to know how high to fill the channel. */
export function terrainBaseDisplacement(x: number, z: number): number {
  let h = Math.sin(x * 0.0008) * 4 + Math.cos(z * 0.0006) * 4 + Math.sin((x + z) * 0.0012) * 2; // broad
  h += Math.sin(x * 0.0021 + 1.3) * Math.cos(z * 0.0017 - 0.6) * 8;   // rolling hills
  h += Math.sin(x * 0.0049) * Math.sin(z * 0.0043) * 2.4;            // small undulation
  // Natural irregularity — smooth fractal relief so the country isn't a tidy sine grid.
  h += fbm2(x * 0.00085, z * 0.00085, 4) * 13;  // broad rolling rise & fall
  h += fbm2(x * 0.0038, z * 0.0038, 3) * 3.2;   // gentle surface roughness
  return h;
}
export function terrainDisplacement(x: number, z: number): number {
  let h = terrainBaseDisplacement(x, z);
  // Carve the river valley.
  const d = riverDist(x, z);
  if (d < RIVER.half) { const t = 1 - d / RIVER.half; h -= RIVER.depth * t * t; }
  // Villages level their ground: blend toward the pad's flat height inside its radius.
  for (let i = 0; i < VILLAGE_PADS.length; i++) {
    const p = VILLAGE_PADS[i];
    const dist = Math.hypot(x - p.x, z - p.z);
    if (dist >= p.r) continue;
    const k = dist <= p.flatR ? 0 : THREE.MathUtils.smoothstep(dist, p.flatR, p.r); // 0 flat → 1 natural
    h = p.level + (h - p.level) * k;
  }
  return h;
}

/** World-space ground height at (x, z). */
export function terrainHeight(x: number, z: number): number {
  return TERRAIN_BASE_Y + terrainDisplacement(x, z);
}

/** Smooth 2D value noise (bilinear-interpolated lattice hash) for rolling terrain relief.
 *  Unlike hash3 below — which is white noise, right for jagged peaks — this is C1-smooth,
 *  so it gives natural undulating ground instead of static. Returns ~[-1, 1]. */
function lhash2(ix: number, iz: number): number {
  const h = Math.sin(ix * 127.1 + iz * 311.7) * 43758.5453;
  return (h - Math.floor(h)) * 2 - 1;
}
function vnoise2(x: number, z: number): number {
  const ix = Math.floor(x), iz = Math.floor(z);
  const fx = x - ix, fz = z - iz;
  const ux = fx * fx * (3 - 2 * fx), uz = fz * fz * (3 - 2 * fz);
  const a = lhash2(ix, iz), b = lhash2(ix + 1, iz);
  const c = lhash2(ix, iz + 1), d = lhash2(ix + 1, iz + 1);
  return (a + (b - a) * ux) * (1 - uz) + (c + (d - c) * ux) * uz;
}
function fbm2(x: number, z: number, octaves: number): number {
  let total = 0, amp = 1, freq = 1, norm = 0;
  for (let o = 0; o < octaves; o++) {
    total += amp * vnoise2(x * freq, z * freq);
    norm += amp; amp *= 0.5; freq *= 2;
  }
  return total / norm;
}

/** Deterministic hash-based 3D value noise — reproducible per-vertex displacement
 *  without pulling in a dependency. Returns [-1, 1]. */
function hash3(x: number, y: number, z: number): number {
  let h = Math.sin(x * 127.1 + y * 311.7 + z * 74.7) * 43758.5453;
  h = h - Math.floor(h);
  return h * 2 - 1;
}
function fbm3(x: number, y: number, z: number, octaves: number): number {
  let total = 0, amp = 1, freq = 1, norm = 0;
  for (let o = 0; o < octaves; o++) {
    total += amp * hash3(x * freq, y * freq, z * freq);
    norm += amp;
    amp *= 0.5;
    freq *= 2;
  }
  return total / norm;
}

/** Build a jagged mountain by starting from a high-res cone and displacing each vertex
 *  outward by fractal noise, then coloring by altitude: rocky brown base → gray mid-rock
 *  → white snowcap. Flat-shaded faces give the crystalline silhouette. */
function buildFractalMountain(base: number, height: number, seed: number): THREE.BufferGeometry {
  const geom = new THREE.ConeGeometry(base, height, 52, 26); // higher res → crisper ridge silhouette
  const pos = geom.attributes.position as THREE.BufferAttribute;
  const halfH = height / 2;
  // Per-mountain snowline (fraction of height): taller peaks wear snow lower. Noise per vertex
  // then breaks the line so caps are ragged, not a clean stripe.
  const snowBase = THREE.MathUtils.clamp(0.72 - (height - 640) / 760 * 0.22, 0.42, 0.72);
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    // Normalized height along axis: 0 at base, 1 at summit.
    const tH = (y + halfH) / height;
    // Radial direction (outward from cone axis)
    const rLen = Math.hypot(x, z) || 1e-6;
    const nx = x / rLen, nz = z / rLen;
    const coordScale = 1 / (base * 0.35);
    // Rounded folds (fbm) blended with RIDGED noise (1-|fbm|) → sharp crests + spurs, not lumps.
    const folds = fbm3(x * coordScale + seed, y * coordScale * 0.7, z * coordScale + seed * 1.3, 5);
    const ridge = 1 - Math.abs(fbm3(x * coordScale * 1.35 + seed * 2.1, y * coordScale * 0.6, z * coordScale * 1.35 + seed * 3.3, 4));
    const n = folds * 0.45 + (ridge - 0.55) * 1.05;
    // Taper displacement so base stays roughly base-wide and the summit stays crisp.
    const radialShape = (1 - tH) * 0.95 + 0.05;
    const disp = n * base * 0.4 * radialShape;
    // Fine scree jitter.
    const jitter = hash3(x * 0.04 + seed * 7, y * 0.05, z * 0.04 + seed * 11) * base * 0.045;
    const newR = rLen + disp + jitter;
    // Keep base from collapsing inward — clamp minimum radius at the foot.
    const minR = base * (1 - tH) * 0.6;
    const finalR = Math.max(minR, newR);
    pos.setX(i, nx * finalR);
    pos.setZ(i, nz * finalR);
    // Vertical: chunk each ridge, and lift crests toward the top so summits read sharp + serrated.
    const vJitter = fbm3(x * 0.003 + seed, y * 0.003, z * 0.003, 3) * height * 0.085 * radialShape;
    // max(0, tH): ConeGeometry's base-ring y is a hair below -halfH (float error), giving tH a tiny
    // NEGATIVE value → Math.pow(neg, 2.2) is NaN → NaN vertex → NaN bounding sphere → the PMREM
    // env map goes NaN and (in stricter drivers like Firefox's) the whole scene renders black.
    const crestLift = Math.pow(Math.max(0, tH), 2.2) * ridge * height * 0.10;
    pos.setY(i, y + vJitter + crestLift);
  }
  // Vertex colors — cool alpine palette (reads well through the blue distance fog) with a ragged
  // snowline and per-vertex hue noise.
  const colors = new Float32Array(pos.count * 3);
  const tmpC = new THREE.Color();
  const rockDark = new THREE.Color(0x46423f);   // dark warm-gray at the foot
  const rockMid = new THREE.Color(0x5c626e);    // cool slate mid-slopes
  const rockHi = new THREE.Color(0x8790a0);     // light blue-gray upper rock
  const snowShad = new THREE.Color(0xcdd8ea);   // bluish snow in shadow
  const snow = new THREE.Color(0xf6faff);       // bright cool snow
  for (let i = 0; i < pos.count; i++) {
    const y = pos.getY(i);
    const tH = THREE.MathUtils.clamp((y + halfH) / height, 0, 1);
    const hueN = hash3(pos.getX(i) * 0.013, y * 0.013, pos.getZ(i) * 0.013);
    // Ragged snowline: perturb where the snow starts, so caps have fingers of rock/snow.
    const snowLine = snowBase + (hueN - 0.5) * 0.14;
    const alt = THREE.MathUtils.clamp(tH + (hueN - 0.5) * 0.06, 0, 1);
    if (alt < snowLine) {
      // rock: dark → mid → hi across the lower band
      if (alt < snowLine * 0.55) tmpC.copy(rockDark).lerp(rockMid, alt / (snowLine * 0.55));
      else tmpC.copy(rockMid).lerp(rockHi, (alt - snowLine * 0.55) / (snowLine * 0.45));
    } else {
      // snow: shadowed base of the cap → bright at the very top
      tmpC.copy(snowShad).lerp(snow, (alt - snowLine) / Math.max(0.001, 1 - snowLine));
    }
    colors[i * 3] = tmpC.r;
    colors[i * 3 + 1] = tmpC.g;
    colors[i * 3 + 2] = tmpC.b;
  }
  geom.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geom.computeVertexNormals();
  return geom;
}

function makeStreet(cx: number, cz: number, angle: number, length: number, width: number): THREE.Mesh {
  // Build the strip in WORLD space, draping each vertex onto the terrain (+ small lift), so
  // the road hugs the rolling ground instead of clipping through it. Many length segments
  // give a smooth drape.
  const geom = new THREE.PlaneGeometry(length, width, 40, 3);
  geom.rotateX(-Math.PI / 2);
  const p = geom.attributes.position as THREE.BufferAttribute;
  const cosA = Math.cos(angle), sinA = Math.sin(angle);
  for (let i = 0; i < p.count; i++) {
    const lx = p.getX(i), lz = p.getZ(i);
    const wx = cx + lx * cosA - lz * sinA;
    const wz = cz + lx * sinA + lz * cosA;
    p.setX(i, wx); p.setZ(i, wz);
    p.setY(i, terrainHeight(wx, wz) + 0.5);
  }
  geom.computeVertexNormals();
  const mat = new THREE.MeshStandardMaterial({
    color: 0x6a5c46, roughness: 1,
    polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -4,
  });
  return new THREE.Mesh(geom, mat); // already in world space — no transform
}

/** Multi-tier conifer: short trunk + 3 stacked cones — a fuller pine than a single cone. */
// --- Trees: lush foliage built from alpha-textured CARDS (small leaf-cluster quads) scattered
//     and oriented through a 3D volume, so a canopy reads as leafy foliage from any angle —
//     including looking straight down from a plane — rather than a faceted geometric solid.
//     Textures are painted procedurally; materials are shared; each tree's cards merge to a
//     single canopy mesh (1 draw call) over a simple trunk. ---
const TRUNK_MAT = new THREE.MeshStandardMaterial({ color: 0x4a3422, roughness: 1, flatShading: true });
const CARD_FWD = new THREE.Vector3(0, 0, 1);

/** LOSSLESS draw-call batcher: collapse a group of many small meshes into one mesh PER MATERIAL,
 *  baking each mesh's world transform into its geometry. Pixels are identical (same vertices, same
 *  materials, and every tree material is opaque or alphaTest so triangle order doesn't matter) — it
 *  only cuts the draw-call count. Used for the static forest, which never animates or gets destroyed. */
function mergeStaticByMaterial(root: THREE.Object3D): THREE.Group {
  root.updateMatrixWorld(true);
  // Bucket by (material, attribute-signature). mergeGeometries requires every input to carry the
  // SAME attribute set, but our meshes are mixed: windows/glass have uv, roofs/boxes may not. So we
  // normalise each geometry to a minimal common set {position, normal} (we never texture-map these —
  // foliage uses its own material with uv intact via its own bucket) before bucketing. This keeps
  // the merge lossless: same vertices, same per-material colour, just fewer draw calls.
  const buckets = new Map<string, { mat: THREE.Material; geos: THREE.BufferGeometry[] }>();
  let key = 0;
  const matKey = new Map<THREE.Material, number>();
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!(m as unknown as { isMesh?: boolean }).isMesh) return;
    if (Array.isArray(m.material)) return;
    let geo = m.geometry.clone();
    if (geo.index) geo = geo.toNonIndexed();
    if (!geo.getAttribute('normal')) geo.computeVertexNormals();
    // Keep uv ONLY for textured materials (foliage cards use map+uv); strip it elsewhere so the
    // attribute sets within a bucket always match.
    const textured = !!(m.material as THREE.MeshStandardMaterial).map;
    for (const name of Object.keys(geo.attributes)) {
      if (name === 'position' || name === 'normal') continue;
      if (name === 'uv' && textured) continue;
      geo.deleteAttribute(name);
    }
    geo.applyMatrix4(m.matrixWorld);
    if (!matKey.has(m.material)) matKey.set(m.material, key++);
    const sig = matKey.get(m.material) + '|' + Object.keys(geo.attributes).sort().join(',');
    let b = buckets.get(sig);
    if (!b) { b = { mat: m.material, geos: [] }; buckets.set(sig, b); }
    b.geos.push(geo);
  });
  const out = new THREE.Group();
  for (const { mat, geos } of buckets.values()) {
    const merged = mergeGeometries(geos, false);
    if (merged) out.add(new THREE.Mesh(merged, mat));
    else for (const gg of geos) out.add(new THREE.Mesh(gg, mat)); // safety net (shouldn't happen now)
  }
  return out;
}

// Rock detail texture (nano-banana) — triplanar-projected onto the mountains for surface grain
// (see makeMountains) so the facets aren't flat solid colours. Repeats + mipmaps for distance.
const ROCK_TEX = new THREE.TextureLoader().load(mountainRockUrl);
ROCK_TEX.wrapS = ROCK_TEX.wrapT = THREE.RepeatWrapping;
ROCK_TEX.anisotropy = 8;

// Painted broadleaf foliage sprites (nano-banana, black keyed to alpha).
const LEAF_TEX = new THREE.TextureLoader().load(leafGreenUrl);
LEAF_TEX.colorSpace = THREE.SRGBColorSpace;
const AUTUMN_TEX = new THREE.TextureLoader().load(leafAutumnUrl);
AUTUMN_TEX.colorSpace = THREE.SRGBColorSpace;

function cardMat(tex: THREE.Texture): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ map: tex, alphaTest: 0.42, side: THREE.DoubleSide, roughness: 1, metalness: 0, vertexColors: true });
}
const LEAF_MAT = cardMat(LEAF_TEX);
const AUTUMN_MAT = cardMat(AUTUMN_TEX);
// Painted fir-bough sprite (nano-banana, black keyed to alpha) — richer than the procedural dabs.
const CONI_FROND_TEX = new THREE.TextureLoader().load(firBoughUrl);
CONI_FROND_TEX.colorSpace = THREE.SRGBColorSpace;
const CONI_FROND_MAT = cardMat(CONI_FROND_TEX);

/** A single foliage card: a quad of "size", rolled, turned to face "dir", placed at "pos". */
function foliageCard(size: number, dir: THREE.Vector3, pos: THREE.Vector3, roll: number): THREE.BufferGeometry {
  const g = new THREE.PlaneGeometry(size, size);
  g.rotateZ(roll);
  g.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(CARD_FWD, dir.clone().normalize()));
  g.translate(pos.x, pos.y, pos.z);
  return g;
}

/** Give a merged canopy a slight per-card brightness mottle (multiplies the texture) for variety. */
/** A frond card: a (non-square) quad facing `dir`, for drooping conifer branches. */
function frondCard(w: number, h: number, dir: THREE.Vector3, pos: THREE.Vector3, roll: number): THREE.BufferGeometry {
  const g = new THREE.PlaneGeometry(w, h);
  g.rotateZ(roll);
  g.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(CARD_FWD, dir.clone().normalize()));
  g.translate(pos.x, pos.y, pos.z);
  return g;
}

function tintCanopy(geo: THREE.BufferGeometry): THREE.BufferGeometry {
  geo.computeBoundingBox();
  const bb = geo.boundingBox!;
  const yLo = bb.min.y, span = Math.max(1e-3, bb.max.y - bb.min.y);
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const col = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const quad = Math.floor(i / 4);
    const s = Math.abs(Math.sin(quad * 12.9898 + 1.3));
    const mottle = 0.88 + 0.12 * (s - Math.floor(s));
    // Vertical ambient occlusion: lower/inner foliage sits in shadow, the crown is sunlit.
    const ty = (pos.getY(i) - yLo) / span;
    const ao = 0.5 + 0.5 * ty * ty;
    const m = mottle * ao;
    col[i * 3] = m; col[i * 3 + 1] = m; col[i * 3 + 2] = m;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
  return geo;
}

/** Broadleaf: simple trunk under a rounded canopy of outward-facing foliage cards. */
export function makeBroadleaf(x: number, gy: number, z: number, autumn = false): THREE.Object3D {
  const grp = new THREE.Group();
  const h = 18 + Math.random() * 12;
  const R = 13 + Math.random() * 6;
  const trunk = new THREE.Mesh(new THREE.CylinderGeometry(1.3, 2.6, h, 6, 1), TRUNK_MAT);
  trunk.position.y = h / 2;
  grp.add(trunk);
  const center = new THREE.Vector3(0, h + R * 0.45, 0);
  const cards: THREE.BufferGeometry[] = [];
  const N = 20;
  for (let k = 0; k < N; k++) {
    const u = (k + 0.5) / N;
    const phi = Math.acos(1 - 1.45 * u);     // bias cards toward the top hemisphere
    const theta = k * 2.399963;              // golden angle → even spread
    const dir = new THREE.Vector3(Math.sin(phi) * Math.cos(theta), Math.cos(phi), Math.sin(phi) * Math.sin(theta));
    const pos = center.clone().addScaledVector(dir, R * 0.42);
    pos.y = center.y + (pos.y - center.y) * 0.82;
    const size = R * (1.0 + Math.random() * 0.45);
    cards.push(foliageCard(size, dir, pos, Math.random() * Math.PI * 2));
  }
  const canopy = new THREE.Mesh(tintCanopy(mergeGeometries(cards, false)!), autumn ? AUTUMN_MAT : LEAF_MAT);
  grp.add(canopy);
  grp.position.set(x, gy, z);
  grp.scale.setScalar(0.85 + Math.random() * 0.45);
  grp.rotation.y = Math.random() * Math.PI * 2;
  return grp;
}

/** Conifer: trunk under a tall conical stack of outward-and-down drooping foliage cards. */
export function makeConifer(x: number, gy: number, z: number): THREE.Object3D {
  const grp = new THREE.Group();
  const trunkH = 9 + Math.random() * 5;
  const H = 58 + Math.random() * 30;
  const baseR = 13 + Math.random() * 4;
  // A thin trunk runs most of the way up so it shows through the gaps between branch tiers.
  const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.7, 1.7, trunkH + H * 0.7, 6, 1), TRUNK_MAT);
  trunk.position.y = (trunkH + H * 0.7) / 2;
  grp.add(trunk);
  const cards: THREE.BufferGeometry[] = [];
  const tiers = 9;
  for (let r = 0; r < tiers; r++) {
    const t = r / (tiers - 1);                       // 0 at the lowest tier, 1 at the apex
    const y = trunkH + t * H;
    const ringR = baseR * (1 - t * 0.82);            // wide base → narrow crown
    const count = Math.max(4, Math.round(9 * (1 - t)) + 2);
    const fl = baseR * 1.7 * (1 - t * 0.5) + 6;      // bough length (hangs down)
    const fw = fl * 0.72;
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2 + r * 0.9;
      const dir = new THREE.Vector3(Math.cos(a) * 0.85, -0.6, Math.sin(a) * 0.85).normalize(); // out & drooping
      const pos = new THREE.Vector3(Math.cos(a) * ringR, y, Math.sin(a) * ringR);
      cards.push(frondCard(fw, fl, dir, pos, (Math.random() - 0.5) * 0.25));
      // Inner fill bough — shorter, nearer the trunk — so the fir reads dense, not stringy.
      const b = a + 0.45;
      const dir2 = new THREE.Vector3(Math.cos(b) * 0.55, -0.5, Math.sin(b) * 0.55).normalize();
      const pos2 = new THREE.Vector3(Math.cos(b) * ringR * 0.55, y + fl * 0.12, Math.sin(b) * ringR * 0.55);
      cards.push(frondCard(fw * 0.66, fl * 0.66, dir2, pos2, (Math.random() - 0.5) * 0.3));
    }
  }
  // Leader spire: a couple of small upright boughs at the very top.
  cards.push(frondCard(baseR * 0.5, baseR * 1.4, new THREE.Vector3(0.15, 1, 0.1).normalize(), new THREE.Vector3(0, trunkH + H, 0), 0));
  cards.push(frondCard(baseR * 0.42, baseR * 1.1, new THREE.Vector3(-0.15, 1, -0.12).normalize(), new THREE.Vector3(0, trunkH + H - 4, 0), 0));
  const canopy = new THREE.Mesh(tintCanopy(mergeGeometries(cards, false)!), CONI_FROND_MAT);
  grp.add(canopy);
  grp.position.set(x, gy, z);
  grp.scale.setScalar(0.82 + Math.random() * 0.4);
  grp.rotation.y = Math.random() * Math.PI * 2;
  return grp;
}

/** Dead/skeletal tree: tapered trunk with a few bare angled branches. */
function makeDeadTree(x: number, gy: number, z: number, bark: THREE.Material): THREE.Group {
  const g = new THREE.Group();
  const h = 36 + Math.random() * 24;
  const trunk = new THREE.Mesh(new THREE.CylinderGeometry(1.2, 3, h, 6), bark);
  trunk.position.y = h / 2;
  g.add(trunk);
  for (let b = 0; b < 4; b++) {
    const len = 9 + Math.random() * 12;
    const branch = new THREE.Mesh(new THREE.CylinderGeometry(0.4, 1.0, len, 5), bark);
    branch.position.y = h - 5 - b * 6;
    branch.rotation.z = (Math.random() - 0.5) * 1.5;
    branch.rotation.y = Math.random() * Math.PI * 2;
    g.add(branch);
  }
  g.position.set(x, gy, z);
  return g;
}

function wallMat(color: number) { return new THREE.MeshStandardMaterial({ color, roughness: 0.95, flatShading: true }); }
function roofMat(color: number) { return new THREE.MeshStandardMaterial({ color, roughness: 0.9, flatShading: true }); }

/** River water: gerstner-rippled surface with fresnel sky reflection, a sharp sun glint and
 *  scene-matched linear fog. Daylight steel-blue to suit the steampunk palette. */
function makeWaterMaterial(sunDir: THREE.Vector3): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    transparent: true,
    side: THREE.DoubleSide,
    uniforms: {
      uTime: { value: 0 },
      uDeep: { value: new THREE.Color(0x14313f) },
      uShallow: { value: new THREE.Color(0x2f6f86) },
      uSky: { value: new THREE.Color(0xa6c8e2) },
      uSun: { value: sunDir.clone().normalize() },
      uSunColor: { value: new THREE.Color(0xfff0d2) },
      uFogColor: { value: new THREE.Color(0xbcd7ee) },
      uFogNear: { value: 2400 },
      uFogFar: { value: 10000 },
    },
    vertexShader: /* glsl */ `
      uniform float uTime;
      varying vec3 vWorldPos;
      varying vec3 vNrm;
      void main() {
        vec3 p = position;
        float w1 = sin(p.x * 0.060 + uTime * 1.7) * 0.55;
        float w2 = sin(p.z * 0.085 - uTime * 1.1) * 0.45;
        float w3 = sin((p.x + p.z) * 0.028 + uTime * 0.7) * 0.70;
        p.y += w1 + w2 + w3;
        float dx = cos(p.x * 0.060 + uTime * 1.7) * 0.060 * 0.55 + cos((p.x + p.z) * 0.028 + uTime * 0.7) * 0.028 * 0.70;
        float dz = cos(p.z * 0.085 - uTime * 1.1) * 0.085 * 0.45 + cos((p.x + p.z) * 0.028 + uTime * 0.7) * 0.028 * 0.70;
        vNrm = normalize(vec3(-dx, 1.0, -dz));
        vec4 wp = modelMatrix * vec4(p, 1.0);
        vWorldPos = wp.xyz;
        gl_Position = projectionMatrix * viewMatrix * wp;
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 uDeep, uShallow, uSky, uSun, uSunColor, uFogColor;
      uniform float uFogNear, uFogFar;
      varying vec3 vWorldPos;
      varying vec3 vNrm;
      void main() {
        vec3 N = normalize(vNrm);
        vec3 V = normalize(cameraPosition - vWorldPos);
        float ndv = max(dot(N, V), 0.0);
        float fres = pow(1.0 - ndv, 3.0);
        vec3 base = mix(uDeep, uShallow, ndv);
        vec3 col = mix(base, uSky, fres * 0.7);
        vec3 H = normalize(normalize(uSun) + V);
        float spec = pow(max(dot(N, H), 0.0), 140.0);
        col += uSunColor * spec * 1.8;
        float fogDepth = length(cameraPosition - vWorldPos);
        col = mix(col, uFogColor, smoothstep(uFogNear, uFogFar, fogDepth));
        gl_FragColor = vec4(col, 0.88);
      }
    `,
  });
}
/** Shared stone plinth so buildings sit on a foundation instead of reading as floating boxes. */
const FOUNDATION_MAT = new THREE.MeshStandardMaterial({ color: 0x6b6056, roughness: 1, flatShading: true });
function foundationBase(w: number, d: number): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w + 2.5, 4.5, d + 2.5, 2, 1, 2), FOUNDATION_MAT);
  m.position.y = 1.6;
  return m;
}
function windowMat() {
  // Small tinted glass — dark by day, slight blue cast. polygonOffset pushes it forward
  // of the wall plane so it doesn't z-fight.
  return new THREE.MeshStandardMaterial({
    color: 0x141a26, roughness: 0.3, metalness: 0.3,
    polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
  });
}
function frameMat() {
  return new THREE.MeshStandardMaterial({
    color: 0x2a1c10, roughness: 0.9,
    polygonOffset: true, polygonOffsetFactor: -3, polygonOffsetUnits: -3,
  });
}

/** Build a window pane: dark glass with a thin wooden frame around it.
 *  Returned as a Group whose +Z axis is the outward face — caller positions and rotates. */
function makeWindow(w: number, h: number): THREE.Group {
  const g = new THREE.Group();
  // Frame: a thin box ring just outside the glass.
  const frameThickness = 0.18;
  const frameDepth = 0.15;
  // Top, bottom, left, right strips of the frame
  const fm = frameMat();
  const top = new THREE.Mesh(new THREE.BoxGeometry(w + frameThickness * 2, frameThickness, frameDepth, 2, 1, 1), fm);
  top.position.set(0, h / 2 + frameThickness / 2, 0);
  g.add(top);
  const bot = top.clone();
  bot.position.set(0, -h / 2 - frameThickness / 2, 0);
  g.add(bot);
  const left = new THREE.Mesh(new THREE.BoxGeometry(frameThickness, h, frameDepth, 1, 2, 1), fm);
  left.position.set(-w / 2 - frameThickness / 2, 0, 0);
  g.add(left);
  const right = left.clone();
  right.position.set(w / 2 + frameThickness / 2, 0, 0);
  g.add(right);
  // Cross mullions (a + dividing the pane into 4 panels)
  const mullV = new THREE.Mesh(new THREE.BoxGeometry(frameThickness * 0.55, h, frameDepth, 1, 2, 1), fm);
  g.add(mullV);
  const mullH = new THREE.Mesh(new THREE.BoxGeometry(w, frameThickness * 0.55, frameDepth, 2, 1, 1), fm);
  g.add(mullH);
  // Glass pane (slightly recessed — small negative offset makes it sit just inside the frame)
  const glass = new THREE.Mesh(new THREE.PlaneGeometry(w, h), windowMat());
  glass.position.z = -0.02;
  g.add(glass);
  return g;
}

/** Proper gabled (triangular-prism) roof: ridge runs along local +Z, base in XZ plane.
 *  Mesh base at y=0, peak at y=height, covers width `w` and depth `d`. */
function makeGableRoof(w: number, d: number, height: number, mat: THREE.Material): THREE.Mesh {
  const hx = w / 2, hz = d / 2;
  const positions = new Float32Array([
    // front triangle (at z = -hz)
    -hx, 0, -hz,   hx, 0, -hz,   0, height, -hz,
    // back triangle (at z = +hz)
    -hx, 0, hz,    hx, 0, hz,    0, height, hz,
    // left slope quad (two tris)
    -hx, 0, -hz,   0, height, -hz,   0, height, hz,
    -hx, 0, -hz,   0, height, hz,    -hx, 0, hz,
    // right slope quad (two tris)
    hx, 0, -hz,    hx, 0, hz,       0, height, hz,
    hx, 0, -hz,    0, height, hz,   0, height, -hz,
  ]);
  // Face winding: CCW as viewed from outside → outward normals after computeVertexNormals.
  const indices = [
    // front: 0,2,1 is CCW viewed from -Z (outside)
    0, 2, 1,
    // back: 3,4,5 is CCW viewed from +Z
    3, 4, 5,
    // left slope: faces -X/+Y; wind so the normal points up-and-out (CCW viewed from outside)
    6, 8, 7,
    9, 11, 10,
    // right slope: faces +X/+Y
    12, 14, 13,
    15, 17, 16,
  ];
  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geom.setIndex(indices);
  geom.computeVertexNormals();
  return new THREE.Mesh(geom, mat);
}

/** Hipped pyramid roof with a rectangular base (w × d) and an apex at (0, height, 0). */
function makeHipRoof(w: number, d: number, height: number, mat: THREE.Material): THREE.Mesh {
  const hx = w / 2, hz = d / 2;
  // 4 base corners + apex
  const positions = new Float32Array([
    -hx, 0, -hz,   hx, 0, -hz,   hx, 0, hz,   -hx, 0, hz,
    0, height, 0,
  ]);
  const A = 0, B = 1, C = 2, D = 3, P = 4;
  const indices = [
    // Four triangular sides, CCW viewed from outside
    A, P, B,  // front (-Z)
    B, P, C,  // right (+X)
    C, P, D,  // back (+Z)
    D, P, A,  // left (-X)
  ];
  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geom.setIndex(indices);
  geom.computeVertexNormals();
  return new THREE.Mesh(geom, mat);
}

function makeCottage(x: number, z: number, wallColor: number, roofColor: number, rotation = Math.random() * Math.PI): THREE.Group {
  const g = new THREE.Group();
  const w = 32 + Math.random() * 18;
  const d = 30 + Math.random() * 18;
  const h = 18 + Math.random() * 10;
  const body = new THREE.Mesh(new THREE.BoxGeometry(w, h, d, 6, 4, 6), wallMat(wallColor));
  body.position.set(0, h / 2, 0);
  g.add(body);
  g.add(foundationBase(w, d));
  const roofH = 12 + Math.random() * 8;
  const roof = makeHipRoof(w * 1.05, d * 1.05, roofH, roofMat(roofColor));
  roof.position.set(0, h, 0);
  g.add(roof);
  // Windows: a pair on each long face at mid-height.
  const windowH = h * 0.4;
  const windowW = w * 0.18;
  const windowY = h * 0.55;
  // Front (-Z) and back (+Z)
  for (const sz of [-d / 2, d / 2]) {
    for (const sx of [-w * 0.25, w * 0.25]) {
      const win = makeWindow(windowW, windowH);
      win.position.set(sx, windowY, sz);
      if (sz > 0) win.rotation.y = Math.PI;
      g.add(win);
    }
  }
  // Side faces: one window each, smaller
  for (const sx of [-w / 2, w / 2]) {
    const win = makeWindow(d * 0.18, windowH * 0.9);
    win.position.set(sx, windowY, 0);
    win.rotation.y = sx > 0 ? Math.PI / 2 : -Math.PI / 2;
    g.add(win);
  }
  // Door on the front
  const door = new THREE.Mesh(new THREE.BoxGeometry(w * 0.16, h * 0.55, 0.3, 2, 4, 1), frameMat());
  door.position.set(0, h * 0.275, -d / 2 - 0.05);
  g.add(door);
  if (Math.random() < 0.7) {
    const chimney = new THREE.Mesh(new THREE.BoxGeometry(2.5, 8, 2.5, 2, 4, 2), wallMat(0x5a4a38));
    chimney.position.set((Math.random() - 0.5) * w * 0.4, h + roofH * 0.6, (Math.random() - 0.5) * d * 0.4);
    g.add(chimney);
  }
  g.position.set(x, terrainHeight(x, z), z);
  g.rotation.y = rotation;
  return g;
}

function makeGabledCottage(x: number, z: number, wallColor: number, roofColor: number, rotation = Math.random() * Math.PI): THREE.Group {
  const g = new THREE.Group();
  const w = 28 + Math.random() * 14;
  const d = 40 + Math.random() * 20;
  const h = 16 + Math.random() * 8;
  const roofH = 10 + Math.random() * 6;

  const body = new THREE.Mesh(new THREE.BoxGeometry(w, h, d, 6, 4, 8), wallMat(wallColor));
  body.position.set(0, h / 2, 0);
  g.add(body);
  g.add(foundationBase(w, d));

  const roof = makeGableRoof(w * 1.05, d * 1.05, roofH, roofMat(roofColor));
  roof.position.set(0, h, 0);
  g.add(roof);

  // Windows: long faces (+/- X) get three down each side.
  const winW = d * 0.13;
  const winH = h * 0.45;
  const winY = h * 0.55;
  for (const sx of [-w / 2, w / 2]) {
    for (const dz of [-d * 0.32, 0, d * 0.32]) {
      const win = makeWindow(winW, winH);
      win.position.set(sx, winY, dz);
      win.rotation.y = sx > 0 ? Math.PI / 2 : -Math.PI / 2;
      g.add(win);
    }
  }
  // Gable-end small window high up
  for (const sz of [-d / 2, d / 2]) {
    const win = makeWindow(w * 0.2, h * 0.18);
    win.position.set(0, h * 0.85, sz);
    if (sz > 0) win.rotation.y = Math.PI;
    g.add(win);
  }
  // Door on a front gable end
  const door = new THREE.Mesh(new THREE.BoxGeometry(w * 0.18, h * 0.6, 0.3, 2, 4, 1), frameMat());
  door.position.set(0, h * 0.3, -d / 2 - 0.05);
  g.add(door);

  g.position.set(x, terrainHeight(x, z), z);
  g.rotation.y = rotation;
  return g;
}

function makeTownhouse(x: number, z: number, wallColor: number, roofColor: number, rotation = Math.random() * Math.PI): THREE.Group {
  const g = new THREE.Group();
  const w = 22 + Math.random() * 12;
  const d = 22 + Math.random() * 12;
  const h = 34 + Math.random() * 14;
  const body = new THREE.Mesh(new THREE.BoxGeometry(w, h, d, 4, 10, 4), wallMat(wallColor));
  body.position.set(0, h / 2, 0);
  g.add(body);
  g.add(foundationBase(w, d));
  const roofH = 6 + Math.random() * 4;
  const roof = makeHipRoof(w * 1.05, d * 1.05, roofH, roofMat(roofColor));
  roof.position.set(0, h, 0);
  g.add(roof);
  // Two storeys of windows on all four faces.
  const stories = 2;
  const colsLR = 2;   // windows per story on left/right faces
  const colsFB = 2;   // windows per story on front/back faces
  const winW = Math.min(w, d) * 0.22;
  const winH = h * 0.18;
  for (let s = 0; s < stories; s++) {
    const sy = h * (0.32 + s * 0.32);
    // Front (-Z) + back (+Z)
    for (const sz of [-d / 2, d / 2]) {
      for (let c = 0; c < colsFB; c++) {
        const sx = -w * 0.3 + (w * 0.6) * (c / Math.max(1, colsFB - 1));
        const win = makeWindow(winW, winH);
        win.position.set(sx, sy, sz);
        if (sz > 0) win.rotation.y = Math.PI;
        g.add(win);
      }
    }
    // Sides (-X, +X)
    for (const sx of [-w / 2, w / 2]) {
      for (let c = 0; c < colsLR; c++) {
        const sz2 = -d * 0.3 + (d * 0.6) * (c / Math.max(1, colsLR - 1));
        const win = makeWindow(winW, winH);
        win.position.set(sx, sy, sz2);
        win.rotation.y = sx > 0 ? Math.PI / 2 : -Math.PI / 2;
        g.add(win);
      }
    }
  }
  // Door at ground level, front face
  const door = new THREE.Mesh(new THREE.BoxGeometry(w * 0.2, h * 0.22, 0.3, 2, 3, 1), frameMat());
  door.position.set(0, h * 0.11, -d / 2 - 0.05);
  g.add(door);
  // Trim stripes between stories
  const trim = new THREE.Mesh(new THREE.BoxGeometry(w * 0.95, 0.6, 0.3, 8, 1, 1), wallMat(0x2a2420));
  trim.position.set(0, h * 0.45, d / 2 + 0.16);
  g.add(trim);
  const trim2 = trim.clone();
  trim2.position.set(0, h * 0.78, d / 2 + 0.16);
  g.add(trim2);
  g.position.set(x, terrainHeight(x, z), z);
  g.rotation.y = rotation;
  return g;
}

function makeBarn(x: number, z: number, wallColor: number, roofColor: number, rotation = Math.random() * Math.PI): THREE.Group {
  const g = new THREE.Group();
  const w = 30 + Math.random() * 18;
  const d = 60 + Math.random() * 30;
  const h = 16 + Math.random() * 6;
  const body = new THREE.Mesh(new THREE.BoxGeometry(w, h, d, 6, 4, 10), wallMat(wallColor));
  body.position.set(0, h / 2, 0);
  g.add(body);
  g.add(foundationBase(w, d));
  const roofH = 9 + Math.random() * 4;
  const roof = makeGableRoof(w * 1.05, d * 1.02, roofH, roofMat(roofColor));
  roof.position.set(0, h, 0);
  g.add(roof);
  // Big main door on the front gable end (+Z face)
  const door = new THREE.Mesh(new THREE.BoxGeometry(w * 0.35, h * 0.7, 0.4, 3, 5, 2), wallMat(0x332217));
  door.position.set(0, h * 0.35, d / 2 + 0.21);
  g.add(door);
  // Hayloft window high in the gable end
  const loft = makeWindow(w * 0.18, h * 0.18);
  loft.position.set(0, h * 0.85, d / 2 + 0.05);
  loft.rotation.y = Math.PI;
  g.add(loft);
  // Small ventilation windows along the long sides
  const winW = Math.min(w, d) * 0.13;
  const winH = h * 0.22;
  for (const sx of [-w / 2, w / 2]) {
    for (let i = 0; i < 4; i++) {
      const sz = -d * 0.4 + (d * 0.8) * (i / 3);
      const win = makeWindow(winW, winH);
      win.position.set(sx, h * 0.65, sz);
      win.rotation.y = sx > 0 ? Math.PI / 2 : -Math.PI / 2;
      g.add(win);
    }
  }
  g.position.set(x, terrainHeight(x, z), z);
  g.rotation.y = rotation;
  return g;
}

function makeLHouse(x: number, z: number, wallColor: number, roofColor: number, rotation = Math.random() * Math.PI): THREE.Group {
  const g = new THREE.Group();
  // Wing A is the long arm; its long axis = local Z (so its ridge runs along Z).
  const wA = 22 + Math.random() * 10;  // narrow side of wing A
  const dA = 38 + Math.random() * 14;  // long side
  // Wing B sticks out perpendicular to wing A — long axis = local X. Its body geometry is
  // built with its long side along X, so the gable roof (ridge along Z by default) needs
  // to be rotated 90° about Y to align with wing B.
  const wB = 28 + Math.random() * 12;  // long side of wing B (along X)
  const dB = 20 + Math.random() * 8;   // narrow side
  const h = 18 + Math.random() * 8;

  const a = new THREE.Mesh(new THREE.BoxGeometry(wA, h, dA, 4, 4, 8), wallMat(wallColor));
  a.position.set(0, h / 2, 0);
  g.add(a);
  // Wing B is offset to one side of wing A, sticking outward in +X.
  const bx = wA / 2 + wB / 2 - wA * 0.35;
  const bz = dA * 0.18;
  const b = new THREE.Mesh(new THREE.BoxGeometry(wB, h, dB, 8, 4, 4), wallMat(wallColor));
  b.position.set(bx, h / 2, bz);
  g.add(b);

  const roofH = 8 + Math.random() * 4;
  // Wing A roof: ridge along +Z (default).
  const rA = makeGableRoof(wA * 1.05, dA * 1.05, roofH, roofMat(roofColor));
  rA.position.set(0, h, 0);
  g.add(rA);
  // Wing B roof: rotate 90° around Y so the ridge runs along +X (perpendicular to wing A).
  const rB = makeGableRoof(dB * 1.05, wB * 1.05, roofH, roofMat(roofColor));
  rB.position.set(bx, h, bz);
  rB.rotation.y = Math.PI / 2;
  g.add(rB);

  // Windows on wing A (long axis Z) — pair on each long side
  const winA = h * 0.45;
  const winW = dA * 0.13;
  for (const sx of [-wA / 2, wA / 2]) {
    for (const sz of [-dA * 0.28, dA * 0.28]) {
      const win = makeWindow(winW, winA);
      win.position.set(sx, h * 0.55, sz);
      win.rotation.y = sx > 0 ? Math.PI / 2 : -Math.PI / 2;
      g.add(win);
    }
  }
  // Windows on wing B (long axis X) — front and back
  const winB = h * 0.4;
  const winWB = wB * 0.13;
  for (const sz of [bz - dB / 2, bz + dB / 2]) {
    for (let i = 0; i < 2; i++) {
      const sx = bx - wB * 0.25 + i * wB * 0.5;
      const win = makeWindow(winWB, winB);
      win.position.set(sx, h * 0.55, sz);
      if (sz > bz) win.rotation.y = Math.PI;
      g.add(win);
    }
  }
  // Door on wing A's front gable end (-Z)
  const door = new THREE.Mesh(new THREE.BoxGeometry(wA * 0.18, h * 0.55, 0.3, 2, 4, 1), frameMat());
  door.position.set(0, h * 0.275, -dA / 2 - 0.05);
  g.add(door);

  g.position.set(x, terrainHeight(x, z), z);
  g.rotation.y = rotation;
  return g;
}

/** TEST ONLY: one of each building type in a labelled row, for inspecting roofs in isolation. */
export function buildBuildingSpecimens(): THREE.Group {
  const g = new THREE.Group();
  const stone = new THREE.MeshStandardMaterial({ color: 0x9a9690, roughness: 1 });
  const slate = new THREE.MeshStandardMaterial({ color: 0x3a3d42, roughness: 1 });
  const wall = 0xb08a5a, roof = 0x8a3b2f;
  const makers: ((x: number, z: number) => THREE.Group)[] = [
    (x, z) => makeCottage(x, z, wall, roof, 0),
    (x, z) => makeGabledCottage(x, z, wall, roof, 0),
    (x, z) => makeTownhouse(x, z, wall, roof, 0),
    (x, z) => makeBarn(x, z, wall, roof, 0),
    (x, z) => makeLHouse(x, z, wall, roof, 0),
    (x, z) => makeChurch(x, z, stone, slate, 0),
  ];
  makers.forEach((m, i) => g.add(m(i * 90 - 225, 0)));
  return g;
}

function makeChurch(x: number, z: number, stone: THREE.Material, slate: THREE.Material, rotation = Math.random() * Math.PI): THREE.Group {
  const g = new THREE.Group();
  // Nave
  const nave = new THREE.Mesh(new THREE.BoxGeometry(28, 26, 54, 4, 6, 8), stone);
  nave.position.set(0, 13, 0);
  g.add(nave);
  // Gable roof over the nave
  const roofH = 10;
  const roof = makeGableRoof(28 * 1.05, 54 * 1.02, roofH, slate);
  roof.position.set(0, 26, 0);
  g.add(roof);
  // Tower base
  const tower = new THREE.Mesh(new THREE.BoxGeometry(14, 55, 14, 4, 12, 4), stone);
  tower.position.set(0, 55 / 2, -23);
  g.add(tower);
  // Tall narrow lancet windows along the nave (a row on each long side)
  for (const sx of [-14, 14]) {
    for (let i = 0; i < 4; i++) {
      const sz = -22 + i * 14.5;
      const win = makeWindow(2.6, 12);
      win.position.set(sx, 13, sz);
      win.rotation.y = sx > 0 ? Math.PI / 2 : -Math.PI / 2;
      g.add(win);
    }
  }
  // Round rose window on the front gable end
  const roseFrame = new THREE.Mesh(new THREE.TorusGeometry(2.4, 0.3, 12, 32), frameMat());
  roseFrame.rotation.y = Math.PI;
  roseFrame.position.set(0, 22, 27.05);
  g.add(roseFrame);
  const roseGlass = new THREE.Mesh(new THREE.CircleGeometry(2.2, 32), windowMat());
  roseGlass.position.set(0, 22, 27.06);
  roseGlass.rotation.y = Math.PI;
  g.add(roseGlass);
  // Belfry opening (dark slice with a louvered hint)
  const belfry = new THREE.Mesh(new THREE.BoxGeometry(8, 10, 8, 4, 4, 4), new THREE.MeshStandardMaterial({ color: 0x1a1612, roughness: 1 }));
  belfry.position.set(0, 48, -23);
  g.add(belfry);
  // Bell-tower windows on each face
  for (const dir of [{ x: 0, z: -23.05, ry: 0 }, { x: 0, z: -22.95, ry: Math.PI }, { x: -7.05, z: -23, ry: -Math.PI / 2 }, { x: 7.05, z: -23, ry: Math.PI / 2 }]) {
    const win = makeWindow(3.5, 6);
    win.position.set(dir.x, 48, dir.z);
    win.rotation.y = dir.ry;
    g.add(win);
  }
  // Big arched main door on the nave front (–Z end of nave)
  const doorWood = new THREE.Mesh(new THREE.BoxGeometry(5, 9, 0.4, 4, 5, 1), wallMat(0x2a1a10));
  doorWood.position.set(0, 4.5, 27.05);
  g.add(doorWood);
  // Spire
  const spire = new THREE.Mesh(new THREE.ConeGeometry(9, 28, 24, 3), slate);
  spire.position.set(0, 55 + 14, -23);
  g.add(spire);
  // Cross on top
  const crossMat = new THREE.MeshStandardMaterial({ color: 0x2a2018, roughness: 0.8 });
  const crossV = new THREE.Mesh(new THREE.BoxGeometry(0.6, 4, 0.6, 2, 4, 2), crossMat);
  crossV.position.set(0, 55 + 28 + 2, -23);
  g.add(crossV);
  const crossH = new THREE.Mesh(new THREE.BoxGeometry(3, 0.6, 0.6, 4, 2, 2), crossMat);
  crossH.position.set(0, 55 + 28 + 2.5, -23);
  g.add(crossH);

  g.position.set(x, terrainHeight(x, z), z);
  g.rotation.y = rotation;
  return g;
}

/** Single-building rubble — a small charred pile in place of one structure. */
function makeBuildingRubble(center: THREE.Vector3, radius: number): THREE.Group {
  const g = new THREE.Group();
  const charred = new THREE.MeshStandardMaterial({ color: 0x3a2c20, roughness: 1, flatShading: true });
  const ash = new THREE.MeshStandardMaterial({ color: 0x1c1814, roughness: 1, flatShading: true });
  // Foundation patch
  const patch = new THREE.Mesh(
    new THREE.PlaneGeometry(radius * 1.6, radius * 1.6),
    new THREE.MeshStandardMaterial({
      color: 0x1d1612, roughness: 1,
      polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1,
    }),
  );
  patch.rotation.x = -Math.PI / 2;
  patch.position.set(center.x, terrainHeight(center.x, center.z) + 0.3, center.z);
  g.add(patch);
  // Tumbled debris within the building footprint, each piece on the terrain under it.
  const count = 8 + Math.floor(Math.random() * 6);
  for (let i = 0; i < count; i++) {
    const a = Math.random() * Math.PI * 2;
    const r = Math.random() * radius * 0.7;
    const w = 0.8 + Math.random() * 2.5;
    const h = 0.4 + Math.random() * 1.4;
    const d = 0.8 + Math.random() * 2.5;
    const px = center.x + Math.cos(a) * r, pz = center.z + Math.sin(a) * r;
    const box = new THREE.Mesh(new THREE.BoxGeometry(w, h, d, 1, 1, 1),
      Math.random() < 0.5 ? charred : ash);
    box.position.set(px, terrainHeight(px, pz) + h / 2 + 0.4, pz);
    box.rotation.y = Math.random() * Math.PI;
    box.rotation.z = (Math.random() - 0.5) * 0.6;
    g.add(box);
  }
  return g;
}

/** Bombed-out village: scattered debris boxes + a few burnt walls + craters.
 *  No persistent smoke (Bomb spawns its own particle plumes via the explosion system). */
function makeRubble(center: THREE.Vector3, radius: number): THREE.Group {
  const g = new THREE.Group();
  const ash = new THREE.MeshStandardMaterial({ color: 0x2a2421, roughness: 1, flatShading: true });
  const charred = new THREE.MeshStandardMaterial({ color: 0x423429, roughness: 1, flatShading: true });
  const splinter = new THREE.MeshStandardMaterial({ color: 0x1c1612, roughness: 1 });
  // Crater (a flat dark disc just above ground)
  const crater = new THREE.Mesh(
    new THREE.CircleGeometry(radius * 0.55, 56),
    new THREE.MeshStandardMaterial({
      color: 0x1d1612, roughness: 1,
      polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1,
    }),
  );
  crater.rotation.x = -Math.PI / 2;
  crater.position.set(center.x, center.y + 1, center.z);
  g.add(crater);
  // ~30 debris boxes randomly tumbled inside the radius
  for (let i = 0; i < 36; i++) {
    const a = Math.random() * Math.PI * 2;
    const r = Math.random() * radius * 0.95;
    const px = center.x + Math.cos(a) * r;
    const pz = center.z + Math.sin(a) * r;
    const w = 1 + Math.random() * 4;
    const h = 0.4 + Math.random() * 1.6;
    const d = 1 + Math.random() * 4;
    const mat = Math.random() < 0.5 ? ash : charred;
    const box = new THREE.Mesh(new THREE.BoxGeometry(w, h, d, 2, 1, 2), mat);
    box.position.set(px, center.y + h / 2 + 0.1, pz);
    box.rotation.y = Math.random() * Math.PI;
    box.rotation.z = (Math.random() - 0.5) * 0.6;
    g.add(box);
  }
  // A few standing burnt walls — tall thin slabs
  for (let i = 0; i < 4; i++) {
    const a = Math.random() * Math.PI * 2;
    const r = Math.random() * radius * 0.55;
    const px = center.x + Math.cos(a) * r;
    const pz = center.z + Math.sin(a) * r;
    const wall = new THREE.Mesh(new THREE.BoxGeometry(0.6, 6 + Math.random() * 6, 4 + Math.random() * 4, 2, 4, 4), charred);
    wall.position.set(px, center.y + 3, pz);
    wall.rotation.y = Math.random() * Math.PI;
    wall.rotation.z = (Math.random() - 0.5) * 0.2;
    g.add(wall);
  }
  // Splintered timber sticking up at random angles
  for (let i = 0; i < 12; i++) {
    const a = Math.random() * Math.PI * 2;
    const r = Math.random() * radius * 0.85;
    const px = center.x + Math.cos(a) * r;
    const pz = center.z + Math.sin(a) * r;
    const stick = new THREE.Mesh(new THREE.BoxGeometry(0.25, 2 + Math.random() * 3, 0.25, 1, 4, 1), splinter);
    stick.position.set(px, center.y + 1.2, pz);
    stick.rotation.x = (Math.random() - 0.5) * 1.2;
    stick.rotation.z = (Math.random() - 0.5) * 1.2;
    stick.rotation.y = Math.random() * Math.PI;
    g.add(stick);
  }
  return g;
}

export function makeCloudTexture(): THREE.Texture {
  const c = document.createElement('canvas');
  c.width = c.height = 256;
  const g = c.getContext('2d')!;
  // Cluster several soft lobes — rounded top, flatter bottom — for a real cumulus silhouette
  // instead of a single fuzzy disc. Randomised so the 3 textures differ.
  const lobes = 6 + Math.floor(Math.random() * 4);
  for (let i = 0; i < lobes; i++) {
    const cx = 128 + (Math.random() - 0.5) * 130;
    const cy = 150 + (Math.random() - 0.5) * 80; // bias downward → flatter base
    const r = 38 + Math.random() * 56;
    const grad = g.createRadialGradient(cx, cy, 2, cx, cy, r);
    grad.addColorStop(0, 'rgba(255,255,255,0.9)');
    grad.addColorStop(0.55, 'rgba(252,253,255,0.34)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, 256, 256);
  }
  // Feather the outer border to ZERO alpha. A lobe's soft falloff can still be ~5–16% opaque where
  // it meets the canvas edge; the sprite quad then hard-cuts it, so the billboard shows as a faint
  // translucent RECTANGLE against dark backdrops (mountains). Multiply in a 24px edge ramp on all
  // four sides (destination-in keeps dest·mask) so the border is provably transparent while the
  // cumulus lobe silhouette inside is preserved.
  const F = 24;
  g.globalCompositeOperation = 'destination-in';
  const ramps: [number, number, number, number][] = [
    [0, 0, 0, F],        // top:    a=0 at y=0 → 1 at y=F
    [0, 256, 0, 256 - F],// bottom: a=0 at y=256 → 1 at y=256-F
    [0, 0, F, 0],        // left
    [256, 0, 256 - F, 0],// right
  ];
  for (const [x0, y0, x1, y1] of ramps) {
    const m = g.createLinearGradient(x0, y0, x1, y1);
    m.addColorStop(0, 'rgba(0,0,0,0)');
    m.addColorStop(1, 'rgba(0,0,0,1)');
    g.fillStyle = m;
    g.fillRect(0, 0, 256, 256);
  }
  g.globalCompositeOperation = 'source-over';
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
